import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMonthlyPerformance } from "../../../services/clearing/station-performance";
import { berlinYearMonth, daysCoveredAt, daysInMonth, MIN_DAYS_COVERED } from "../utils/projection";
import type {
  ClosureState,
  MonthlyReport,
  PerformanceUnit,
  ReportingOptions,
} from "../../../types/stationPerformance";

/** The API caps `limit` at 200. Totals are summed from rows, so this bound matters: see `partial`. */
const PAGE_LIMIT = 200;

/** The month before a "YYYY-MM" one; January rolls back to December of the previous year. */
const previousOf = (yearMonth: string) => {
  const [y, m] = yearMonth.split("-").map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, "0")}`;
};

export interface MonthSummary {
  month: string;
  moneyCents: number;
  cochargeMoneyCents: number;
  /** The parts of `cochargeMoneyCents` (TOTAL basis): fee + internal revenue + THG share. */
  cochargeFeeCents: number;
  internalRevenueCents: number;
  /** Null when this month's year has no THG price configured (not zero: unknown). */
  thgTotalCents: number | null;
  thgCochargeCents: number | null;
  thgPayoutCents: number | null;
  /**
   * True under the FREE_ONLY segment: free charging earns no cash, so the backend sends null THG and
   * the figures are "not applicable", which is a different state from "no price configured".
   */
  thgNotApplicable: boolean;
  /** Years in this month's report with no THG price; THG is left out of the sums above. */
  thgMissingYears: number[];
  sessions: number;
  energyKwh: number;
  freeEnergyKwh: number;
  paidEnergyKwh: number;
  /** Units the fleet actually has this month, from `totalItems`: not the number summed. */
  activeUnits: number;
  topUnit: PerformanceUnit | null;
  /**
   * True when the fleet is bigger than one page, so the totals above cover only the first
   * {@link PAGE_LIMIT} units. Surfaced rather than swallowed: an understated headline that looks
   * authoritative is worse than one that admits it is partial.
   */
  partial: boolean;
  closure: ClosureState | null;
  liveDayIncluded: boolean;
  /** The live cocharge fee percent from the report context, for the fee label; absent when not sent. */
  cochargeFeePercent?: number | null;
  /** Not computed yet: a distinct state from "no data", so the caller can say which it is. */
  neverComputed: boolean;
}

export interface DashboardSummary {
  current: MonthSummary | null;
  previous: MonthSummary | null;
  /** `YYYY-MM` keys, so a caller can read the same two months through another endpoint. */
  currentMonth: string;
  previousMonth: string;
  /** True when the analysed month is the running Berlin month; false for a finished (past) month. */
  isCurrentMonth: boolean;
  /**
   * The previous month has no data (never computed, stale or absent from the report's computed months).
   * Its figures are then unknown, not zero, and no comparison is made against it.
   */
  previousMissing: boolean;
  /**
   * The previous month's closure state as the current month's report states it. Kept apart from
   * `previous.closure` so a missing month still shows its state.
   */
  previousClosure?: ClosureState | null;
  /**
   * Days of the analysed month the figures cover, as of {@link asOf}: completed days plus the elapsed part of
   * today (Berlin minutes ÷ 1440) when today's sessions are included; all of them for a finished month.
   */
  daysCovered: number;
  /** When the figures were read; the elapsed part of today is counted up to this moment. */
  asOf: Date;
  daysInCurrentMonth: number;
  daysInPreviousMonth: number;
  /**
   * Last month's money scaled straight-line to the same number of covered days. An estimate, not
   * measured: no daily money series is stored, only daily session counts. Named so the UI can say so.
   * Null for the running month while fewer than {@link MIN_DAYS_COVERED} days are covered.
   */
  previousPaceCents: number | null;
  /** Positive = ahead of last month at the same point. Null when either month is unavailable. */
  paceDeltaPercent: number | null;
  /** Union of both months' years without a THG price, ascending. */
  thgMissingYears: number[];
}

const summarise = (report: MonthlyReport, month: string): MonthSummary => {
  const units = report.units ?? [];
  const sum = (pick: (u: PerformanceUnit) => number) => units.reduce((t, u) => t + (pick(u) || 0), 0);
  const thgNotApplicable = report.context?.options?.segment === "FREE_ONLY";
  // Not-applicable wins over missing prices: nothing is left out of a segment that has no THG money.
  const thgMissingYears = thgNotApplicable ? [] : (report.context?.thgPricesMissingYears ?? []);
  const yearMissing = thgMissingYears.includes(Number(month.slice(0, 4)));
  const sumThg = (pick: (u: PerformanceUnit) => number | null | undefined) =>
    thgNotApplicable || yearMissing ? null : units.reduce((t, u) => t + (pick(u) ?? 0), 0);
  return {
    month,
    moneyCents: sum((u) => u.moneyCents),
    cochargeMoneyCents: sum((u) => u.cochargeMoneyCents),
    cochargeFeeCents: sum((u) => u.cochargeFeeCents),
    internalRevenueCents: sum((u) => u.internalRevenueCents),
    thgTotalCents: sumThg((u) => u.thgTotalCents),
    thgCochargeCents: sumThg((u) => u.thgCochargeCents),
    thgPayoutCents: sumThg((u) => u.thgPayoutCents),
    thgNotApplicable,
    thgMissingYears,
    sessions: sum((u) => u.sessions),
    energyKwh: sum((u) => u.energyKwh),
    freeEnergyKwh: sum((u) => u.freeEnergyKwh),
    paidEnergyKwh: sum((u) => u.paidEnergyKwh),
    activeUnits: report.totalItems ?? units.length,
    // Rows arrive sorted by money desc, so the first row is the top earner for the whole month even
    // when the totals above are only a partial page.
    topUnit: units[0] ?? null,
    partial: (report.totalItems ?? 0) > units.length,
    closure: report.context?.monthClosure?.[month] ?? null,
    liveDayIncluded: Boolean(report.context?.liveDayIncluded),
    cochargeFeePercent: report.context?.cochargeFeePercent ?? report.context?.cochargeSharePercent ?? null,
    neverComputed: false,
  };
};

const notComputed = (month: string): MonthSummary => ({
  month,
  moneyCents: 0,
  cochargeMoneyCents: 0,
  cochargeFeeCents: 0,
  internalRevenueCents: 0,
  thgTotalCents: null,
  thgCochargeCents: null,
  thgPayoutCents: null,
  thgNotApplicable: false,
  thgMissingYears: [],
  sessions: 0,
  energyKwh: 0,
  freeEnergyKwh: 0,
  paidEnergyKwh: 0,
  activeUnits: 0,
  topUnit: null,
  partial: false,
  closure: null,
  liveDayIncluded: false,
  neverComputed: true,
});

/**
 * The two months the dashboard compares: the analysed month and the one before it.
 *
 * For the running Berlin month the comparison is pace-adjusted (last month scaled to the same number of
 * elapsed days). For any finished month it is whole against whole, since both months are complete.
 *
 * Both months are fetched independently so one uncomputed month degrades a card instead of the page.
 */
export const useDashboardSummary = (options: ReportingOptions, month: string, now?: Date) => {
  const [current, setCurrent] = useState<MonthSummary | null>(null);
  const [previous, setPrevious] = useState<MonthSummary | null>(null);
  const [previousMissing, setPreviousMissing] = useState(false);
  const [previousClosure, setPreviousClosure] = useState<ClosureState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The moment the shown figures were read (BOF-84): a tab left open keeps its comparison until reload.
  const [loadedAt, setLoadedAt] = useState<Date>(() => now ?? new Date());
  const asOf = now ?? loadedAt;

  const { previousMonth, isCurrentMonth, daysInCurrentMonth, daysInPreviousMonth } = useMemo(
    () => ({
      previousMonth: previousOf(month),
      isCurrentMonth: berlinYearMonth(asOf) === month,
      daysInCurrentMonth: daysInMonth(month),
      daysInPreviousMonth: daysInMonth(previousOf(month)),
    }),
    [month, asOf],
  );

  // Month and options can change while requests are in flight: only the newest load may write state.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    const isStale = () => id !== requestId.current;
    setLoading(true);
    setError(null);
    const requestedAt = new Date();

    let currentContext: MonthlyReport["context"] | undefined;
    const fetchMonth = async (target: string): Promise<MonthSummary | null> => {
      const result = await getMonthlyPerformance({
        month: target,
        options,
        sort: "moneyCents",
        order: "desc",
        limit: PAGE_LIMIT,
      });
      if (result?.success && result.data) {
        if (target === month) currentContext = result.data.context;
        return summarise(result.data, target);
      }
      if (result?.neverComputed) return notComputed(target);
      if (isStale()) return null;
      setError(result?.message ?? "Could not load the performance summary.");
      return null;
    };

    let c: MonthSummary | null;
    let p: MonthSummary | null;
    try {
      [c, p] = await Promise.all([fetchMonth(month), fetchMonth(previousMonth)]);
    } catch {
      // A throwing fetch must not leave the page loading forever.
      if (isStale()) return;
      setError("Could not load the performance summary.");
      setLoading(false);
      return;
    }
    if (isStale()) return;
    // Missing = never computed, or the report itself says it is stale / not among the computed months.
    const missing = Boolean(
      p?.neverComputed ||
        currentContext?.staleMonths?.includes(previousMonth) ||
        (currentContext?.monthsComputed?.length && !currentContext.monthsComputed.includes(previousMonth)),
    );
    setLoadedAt(requestedAt);
    setCurrent(c);
    // Unknown, not zero: a month the report flags as missing never feeds a figure or a comparison.
    setPrevious(missing && p ? notComputed(previousMonth) : p);
    setPreviousMissing(missing);
    setPreviousClosure(currentContext?.monthClosure?.[previousMonth] ?? null);
    setLoading(false);
  }, [month, previousMonth, options]);

  useEffect(() => {
    load();
    // Unmount (or a new load) invalidates whatever is still in flight.
    return () => {
      requestId.current += 1;
    };
  }, [load]);

  const summary: DashboardSummary = useMemo(() => {
    const usable = (s: MonthSummary | null) => (s && !s.neverComputed ? s : null);
    const c = usable(current);
    const p = usable(previous);
    // The same days the projection divides by: today counts only as far as it has gone, and only when
    // today's sessions are in the figures.
    const daysCovered = isCurrentMonth ? daysCoveredAt(asOf, Boolean(c?.liveDayIncluded)) : daysInCurrentMonth;
    // Running month: last month scaled to the same covered days. Finished month: whole vs whole.
    const previousPaceCents =
      p && daysInPreviousMonth > 0
        ? isCurrentMonth
          ? daysCovered >= MIN_DAYS_COVERED
            ? Math.round(p.moneyCents * (Math.min(daysCovered, daysInCurrentMonth) / daysInPreviousMonth))
            : null
          : p.moneyCents
        : null;
    const thgMissingYears = Array.from(
      new Set([...(c?.thgMissingYears ?? []), ...(p?.thgMissingYears ?? [])]),
    ).sort((a, b) => a - b);
    return {
      current,
      previous,
      thgMissingYears,
      currentMonth: month,
      previousMonth,
      isCurrentMonth,
      previousMissing,
      previousClosure,
      daysCovered,
      asOf,
      daysInCurrentMonth,
      daysInPreviousMonth,
      previousPaceCents,
      paceDeltaPercent:
        c && previousPaceCents && previousPaceCents > 0
          ? ((c.moneyCents - previousPaceCents) / previousPaceCents) * 100
          : null,
    };
  }, [
    current,
    previous,
    previousMissing,
    previousClosure,
    month,
    previousMonth,
    isCurrentMonth,
    asOf,
    daysInCurrentMonth,
    daysInPreviousMonth,
  ]);

  return { summary, loading, error, reload: load };
};
